import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, ChevronUp, ChevronDown, Trash2, Pencil, ArrowLeft, Eye, EyeOff, X, Check } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useCounters } from "../../contexts/CountersContext";

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function ProgressBar({ pct, large = false }) {
    return (
        <div className={"lt-progress-bar" + (large ? " lt-progress-bar--lg" : "")} data-testid="cl-progress-bar">
            <div className="lt-progress-bar__fill" style={{ width: `${pct}%` }} />
        </div>
    );
}
function VisibilityBadge({ visible, hasClient }) {
    if (visible && hasClient) return (
        <span className="lt-visibility lt-visibility--public" data-testid="cl-vis-public">
            <Eye size={12} strokeWidth={2} /> Visibile al cliente
        </span>
    );
    return (
        <span className="lt-visibility lt-visibility--internal" data-testid="cl-vis-internal">
            <EyeOff size={12} strokeWidth={2} /> Interna
        </span>
    );
}

function ChecklistForm({ initial, clients, categories, onSave, onClose, saving }) {
    const isEdit = Boolean(initial?.id);
    const [title, setTitle] = useState(initial?.title || "");
    const [categoryId, setCategoryId] = useState(initial?.category?.id || "");
    const [clientId, setClientId] = useState(initial?.client?.id || "");
    const [visible, setVisible] = useState(Boolean(initial?.visible_to_client));
    const [itemsText, setItemsText] = useState("");
    const generale = !clientId;

    const submit = (e) => {
        e.preventDefault();
        if (!title.trim() || !categoryId) { toast.error("Titolo e categoria obbligatori"); return; }
        const payload = { title: title.trim(), category_id: categoryId };
        if (clientId) { payload.client_id = clientId; payload.visible_to_client = visible; }
        else { payload.client_id = null; payload.visible_to_client = false; }
        if (!isEdit && itemsText.trim()) {
            payload.items = itemsText.split("\n").map(s => s.trim()).filter(Boolean).map(t => ({ text: t }));
        }
        onSave(payload);
    };

    return (
        <form onSubmit={submit} className="lt-scad-form" data-testid="cl-form">
            <div className="lt-form-row">
                <label className="lt-label">Titolo *</label>
                <input className="lt-input" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={200} data-testid="cl-form-title" />
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Categoria *</label>
                <select className="lt-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required data-testid="cl-form-category">
                    <option value="">Seleziona…</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Cliente</label>
                <select className="lt-select" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="cl-form-client">
                    <option value="">Nessuno (generale)</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
            </div>
            {!isEdit && (
                <div className="lt-form-row">
                    <label className="lt-label">Passi iniziali (uno per riga, opzionale)</label>
                    <textarea className="lt-textarea" rows={4} value={itemsText} onChange={(e) => setItemsText(e.target.value)} placeholder="Passo 1&#10;Passo 2&#10;…" data-testid="cl-form-items" />
                </div>
            )}
            <div className="lt-toggle-row">
                <label className="lt-toggle">
                    <input type="checkbox" checked={visible && !generale} disabled={generale} onChange={(e) => setVisible(e.target.checked)} data-testid="cl-form-vis" />
                    <span className="lt-toggle__track"><span className="lt-toggle__thumb" /></span>
                    <span className="lt-toggle__label">Visibile al cliente</span>
                </label>
                <p className="lt-toggle__hint">{generale ? "Disponibile solo se assegnata a un cliente." : "Quando attivo, la checklist appare nel portale del cliente."}</p>
            </div>
            <div className="lt-form-actions">
                <div style={{ flex: 1 }} />
                <button type="button" className="lt-btn-secondary" onClick={onClose}>Annulla</button>
                <button type="submit" className="lt-btn-primary" disabled={saving} data-testid="cl-form-save">{isEdit ? "Salva" : "Crea"}</button>
            </div>
        </form>
    );
}

export function ChecklistList() {
    const nav = useNavigate();
    const { counters, refresh: refreshCounters } = useCounters();
    const cc = counters?.checklist || {};
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [cats, setCats] = useState([]);
    const [scope, setScope] = useState("all");
    const [q, setQ] = useState("");
    const [clientF, setClientF] = useState("");
    const [catF, setCatF] = useState("");
    const [visF, setVisF] = useState("all");
    const [showNew, setShowNew] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = { scope, page: 1, page_size: 100, visible_to_client: visF };
            if (q.trim()) params.q = q.trim();
            if (clientF) params.client_id = clientF;
            if (catF) params.category_id = catF;
            const { data } = await api.get("/internal/checklist", { params });
            setItems(data.items);
        } catch { toast.error("Errore caricamento checklist"); }
    }, [scope, q, clientF, catF, visF]);

    useEffect(() => {
        api.get("/internal/clients", { params: { status: "active", page_size: 200 } }).then(({ data }) => setClients(data.items || data)).catch(() => {});
        api.get("/internal/checklist-categories", { params: { active: "all" } }).then(({ data }) => setCats(data.items || [])).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async (payload) => {
        setSaving(true);
        try { const { data } = await api.post("/internal/checklist", payload); toast.success("Checklist creata"); setShowNew(false); load(); refreshCounters(); nav(`/app/checklist/${data.id}`); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
    };
    const doDelete = async () => {
        try { await api.delete(`/internal/checklist/${confirmDel.id}`); toast.success("Checklist eliminata"); setConfirmDel(null); load(); refreshCounters(); }
        catch { toast.error("Errore"); }
    };

    const counterCards = useMemo(() => [
        { key: "totali", label: "Totali", value: cc.totali ?? 0 },
        { key: "generali", label: "Generali", value: cc.generali ?? 0 },
        { key: "per_cliente", label: "Per cliente", value: cc.per_cliente ?? 0 },
        { key: "visibili", label: "Visibili al cliente", value: cc.visibili_al_cliente ?? 0 },
    ], [cc]);

    return (
        <div style={{ padding: "32px 40px" }} data-testid="cl-page">
            <div className="flex items-center" style={{ marginBottom: 20 }}>
                <h1 className="lt-h1" style={{ flex: 1 }}>Checklist</h1>
                <button className="lt-btn-primary" onClick={() => setShowNew(true)} data-testid="cl-new-btn"><Plus size={14} /> Nuova checklist</button>
            </div>
            <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }} data-testid="cl-counters">
                {counterCards.map((c) => (
                    <div key={c.key} className="lt-counter-card" style={{ cursor: "default" }} data-testid={`cl-counter-${c.key}`}>
                        <div className="lt-counter-card__value">{c.value}</div>
                        <div className="lt-counter-card__label">{c.label}</div>
                    </div>
                ))}
            </div>
            <div className="flex items-center" style={{ gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                <input type="search" className="lt-input" placeholder="Cerca titolo…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} data-testid="cl-search" />
                <div className="lt-segmented" data-testid="cl-scope">
                    {[["all", "Tutte"], ["general", "Generali"], ["client", "Con cliente"]].map(([k, l]) => (
                        <button key={k} type="button" className={"lt-segmented__btn" + (scope === k ? " is-active" : "")} onClick={() => setScope(k)}>{l}</button>
                    ))}
                </div>
                <select className="lt-select" value={clientF} onChange={(e) => setClientF(e.target.value)}>
                    <option value="">Tutti i clienti</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
                <select className="lt-select" value={catF} onChange={(e) => setCatF(e.target.value)}>
                    <option value="">Tutte le categorie</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select className="lt-select" value={visF} onChange={(e) => setVisF(e.target.value)}>
                    <option value="all">Tutte</option>
                    <option value="true">Visibili al cliente</option>
                    <option value="false">Interne</option>
                </select>
            </div>
            <div className="lt-card" style={{ padding: 0 }}>
                <table className="lt-table">
                    <thead>
                        <tr>
                            <th>Titolo</th>
                            <th style={{ width: 140 }}>Categoria</th>
                            <th style={{ width: 180 }}>Cliente</th>
                            <th style={{ width: 180 }}>Progresso</th>
                            <th style={{ width: 170 }}>Visibilità</th>
                            <th style={{ width: 60 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: 24, color: "#8A8F88" }}>Nessuna checklist con i filtri correnti.</td></tr>
                        ) : items.map((it) => (
                            <tr key={it.id} data-testid={`cl-row-${it.id}`} onClick={() => nav(`/app/checklist/${it.id}`)} style={{ cursor: "pointer" }}>
                                <td style={{ color: "#181B20", fontWeight: 500 }}>{it.title}</td>
                                <td style={{ color: "#5F6560" }}>{it.category?.name || "—"}</td>
                                <td style={{ color: "#5F6560" }}>{it.client?.company_name || <span style={{ color: "#8A8F88" }}>—</span>}</td>
                                <td>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <span style={{ fontWeight: 500, fontSize: 13 }}>{it.items_done}/{it.items_total}</span>
                                        <div style={{ flex: 1 }}><ProgressBar pct={it.progress_percent} /></div>
                                    </div>
                                </td>
                                <td><VisibilityBadge visible={it.visible_to_client} hasClient={Boolean(it.client)} /></td>
                                <td onClick={(e) => e.stopPropagation()}>
                                    <button className="lt-icon-btn" onClick={() => setConfirmDel({ id: it.id, title: it.title })} title="Elimina" data-testid={`cl-delete-${it.id}`}><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showNew && (
                <div className="lt-modal-backdrop" onClick={() => setShowNew(false)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="cl-new-modal">
                        <div className="lt-modal__head"><h2 className="lt-modal__title">Nuova checklist</h2>
                            <button className="lt-icon-btn" onClick={() => setShowNew(false)}><X size={16} /></button></div>
                        <ChecklistForm clients={clients} categories={cats.filter(c => c.active)} onSave={create} onClose={() => setShowNew(false)} saving={saving} />
                    </div>
                </div>
            )}
            {confirmDel && (
                <div className="lt-modal-backdrop" onClick={() => setConfirmDel(null)}>
                    <div className="lt-modal lt-modal--small" onClick={(e) => e.stopPropagation()}>
                        <div className="lt-confirm">
                            <h3 className="lt-confirm__title">Eliminare la checklist?</h3>
                            <p className="lt-confirm__sub">«{confirmDel.title}» e tutti i suoi passi verranno rimossi.</p>
                            <div className="lt-form-actions">
                                <div style={{ flex: 1 }} />
                                <button className="lt-btn-secondary" onClick={() => setConfirmDel(null)}>Annulla</button>
                                <button className="lt-btn-danger" onClick={doDelete}>Elimina</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ChecklistDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [clients, setClients] = useState([]);
    const [cats, setCats] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [confirmDel, setConfirmDel] = useState(false);
    const [newItemText, setNewItemText] = useState("");
    const [editingItemId, setEditingItemId] = useState(null);
    const [editingItemText, setEditingItemText] = useState("");
    const [saving, setSaving] = useState(false);
    const { refresh: refreshCounters } = useCounters();

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/internal/checklist/${id}`); setData(data); }
        catch { toast.error("Checklist non trovata"); nav("/app/checklist"); }
    }, [id, nav]);
    useEffect(() => {
        load();
        api.get("/internal/clients", { params: { status: "active", page_size: 200 } }).then(({ data }) => setClients(data.items || data));
        api.get("/internal/checklist-categories", { params: { active: "true" } }).then(({ data }) => setCats(data.items || []));
    }, [load]);

    if (!data) return <div style={{ padding: 40, color: "#8A8F88" }}>Caricamento…</div>;

    const tick = async (iid, completed) => {
        try {
            await api.post(`/internal/checklist/${id}/items/${iid}/tick`, { completed });
            load(); refreshCounters();
        } catch { toast.error("Errore"); }
    };
    const addItem = async () => {
        if (!newItemText.trim()) return;
        try { await api.post(`/internal/checklist/${id}/items`, { text: newItemText.trim() }); setNewItemText(""); load(); }
        catch { toast.error("Errore"); }
    };
    const patchItem = async (iid, text) => {
        try { await api.patch(`/internal/checklist/${id}/items/${iid}`, { text: text.trim() }); setEditingItemId(null); load(); }
        catch { toast.error("Errore"); }
    };
    const delItem = async (iid) => {
        try { await api.delete(`/internal/checklist/${id}/items/${iid}`); load(); refreshCounters(); }
        catch { toast.error("Errore"); }
    };
    const moveItem = async (idx, dir) => {
        const ids = data.items.map(it => it.id);
        const target = idx + dir;
        if (target < 0 || target >= ids.length) return;
        [ids[idx], ids[target]] = [ids[target], ids[idx]];
        try { await api.post(`/internal/checklist/${id}/items/reorder`, { item_ids: ids }); load(); }
        catch { toast.error("Errore riordino"); }
    };
    const saveEdit = async (payload) => {
        setSaving(true);
        try { await api.patch(`/internal/checklist/${id}`, payload); toast.success("Checklist aggiornata"); setShowEdit(false); load(); refreshCounters(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
    };
    const doDelete = async () => {
        try { await api.delete(`/internal/checklist/${id}`); toast.success("Eliminata"); refreshCounters(); nav("/app/checklist"); }
        catch { toast.error("Errore"); }
    };

    return (
        <div style={{ padding: "32px 40px", maxWidth: 900 }} data-testid="cl-detail-page">
            <Link to="/app/checklist" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16 }}><ArrowLeft size={14} /> Torna alle checklist</Link>
            <div className="flex items-start" style={{ gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h1 className="lt-h1" data-testid="cl-detail-title">{data.title}</h1>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                        <span className="lt-chip lt-chip--inactive">{data.category?.name || "—"}</span>
                        <VisibilityBadge visible={data.visible_to_client} hasClient={Boolean(data.client)} />
                        <span className="lt-sub" style={{ fontSize: 13 }}>{data.client ? data.client.company_name : "Generale"} · {data.items_done}/{data.items_total} · {data.progress_percent}%</span>
                    </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button className="lt-btn-secondary" onClick={() => setShowEdit(true)} data-testid="cl-detail-edit"><Pencil size={14} /> Modifica</button>
                    <button className="lt-btn-danger-text" onClick={() => setConfirmDel(true)}><Trash2 size={14} /> Elimina</button>
                </div>
            </div>
            <ProgressBar pct={data.progress_percent} large />
            <div className="lt-card" style={{ padding: 8, marginTop: 20 }} data-testid="cl-items">
                {data.items.map((it, idx) => (
                    <div key={it.id} className="lt-item-row" data-testid={`cl-item-${it.id}`}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <button className="lt-icon-btn" onClick={() => moveItem(idx, -1)} disabled={idx === 0} title="Sposta su"><ChevronUp size={14} /></button>
                            <button className="lt-icon-btn" onClick={() => moveItem(idx, 1)} disabled={idx === data.items.length - 1} title="Sposta giù"><ChevronDown size={14} /></button>
                        </div>
                        <label className={"lt-checkbox" + (it.completed ? " is-checked" : "")} onClick={() => tick(it.id, !it.completed)} data-testid={`cl-item-check-${it.id}`}>
                            {it.completed && <Check size={12} strokeWidth={3} color="#181B20" />}
                        </label>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {editingItemId === it.id ? (
                                <div style={{ display: "flex", gap: 8 }}>
                                    <input className="lt-input" value={editingItemText} onChange={(e) => setEditingItemText(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === "Enter") patchItem(it.id, editingItemText); if (e.key === "Escape") setEditingItemId(null); }} />
                                    <button className="lt-btn-primary" onClick={() => patchItem(it.id, editingItemText)}>Salva</button>
                                </div>
                            ) : (
                                <>
                                    <div style={it.completed ? { textDecoration: "line-through", color: "#8A8F88" } : { color: "#181B20" }}>{it.text}</div>
                                    {it.completed && it.completed_by && <div style={{ fontSize: 12, color: "#8A8F88", marginTop: 2 }}>Completato da {it.completed_by.name} · {fmtDateTime(it.completed_at)}</div>}
                                </>
                            )}
                        </div>
                        <button className="lt-icon-btn" onClick={() => { setEditingItemId(it.id); setEditingItemText(it.text); }} title="Modifica"><Pencil size={14} /></button>
                        <button className="lt-icon-btn" onClick={() => delItem(it.id)} title="Elimina"><Trash2 size={14} /></button>
                    </div>
                ))}
                <div className="lt-item-row" style={{ borderTop: "1px solid #E3E5E1", marginTop: 4, paddingTop: 12 }}>
                    <div style={{ width: 24 }} />
                    <input className="lt-input" placeholder="Aggiungi nuovo passo…" value={newItemText} onChange={(e) => setNewItemText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addItem(); }} data-testid="cl-item-add-input" />
                    <button className="lt-btn-primary" onClick={addItem} data-testid="cl-item-add-btn"><Plus size={14} /></button>
                </div>
            </div>

            {showEdit && (
                <div className="lt-modal-backdrop" onClick={() => setShowEdit(false)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="cl-edit-modal">
                        <div className="lt-modal__head"><h2 className="lt-modal__title">Modifica checklist</h2>
                            <button className="lt-icon-btn" onClick={() => setShowEdit(false)}><X size={16} /></button></div>
                        <ChecklistForm initial={data} clients={clients} categories={cats} onSave={saveEdit} onClose={() => setShowEdit(false)} saving={saving} />
                    </div>
                </div>
            )}
            {confirmDel && (
                <div className="lt-modal-backdrop" onClick={() => setConfirmDel(false)}>
                    <div className="lt-modal lt-modal--small" onClick={(e) => e.stopPropagation()}>
                        <div className="lt-confirm">
                            <h3 className="lt-confirm__title">Eliminare la checklist?</h3>
                            <p className="lt-confirm__sub">«{data.title}» e tutti i suoi passi verranno rimossi.</p>
                            <div className="lt-form-actions">
                                <div style={{ flex: 1 }} />
                                <button className="lt-btn-secondary" onClick={() => setConfirmDel(false)}>Annulla</button>
                                <button className="lt-btn-danger" onClick={doDelete}>Elimina</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
